import type { EventParticipantFeedback } from 'models';
import { Modal, Skeleton, Text, handleAxiosError, message } from 'ui';
import { useUser } from 'features/auth';
import { useCreateNodeEventFeedbackMutation, useNodeEventQuery } from '../../queries';
import { type EventFeedbackConfig, RateEventFeedbackForm } from '../rate-event-feedback-form';

type P = {
  eventId: string;
  isOpened: boolean;
  onCancel: () => void;
  onSuccess: () => void;
};

const config: EventFeedbackConfig[] = [
  {
    key: 'recommendationLikelihood',
    starLabel: 'What is your likelihood of recommending this service to friends or family?',
    textAreaLabel: 'What would improve your experience with this service?',
    textKey: 'recommendationLikelihoodText',
    title: 'Rate the Service',
  },
  {
    key: 'providerExperience',
    starLabel: 'How would you rate your experience with your provider today?',
    textAreaLabel: 'What would improve your experience with your provider?',
    textKey: 'providerExperienceText',
    title: 'Rate the Provider',
  },
  {
    key: 'isAudioOk',
    starLabel: 'How would you rate the audio quality?',
    textAreaLabel: 'What would improve your audio quality?',
    textKey: 'audioText',
    title: 'Rate the Audio Quality',
  },
  {
    key: 'isVideoOk',
    starLabel: 'How would you rate the video quality?',
    textAreaLabel: 'What would improve your video quality?',
    textKey: 'videoText',
    title: 'Rate the Video Quality',
  },
];

const RateEventFeedbackModal = ({ eventId, isOpened, onCancel, onSuccess }: P) => {
  const { isPending: isCreatingEventFeedback, mutate: createTelehealthEventFeedback } =
    useCreateNodeEventFeedbackMutation();
  const { data, isLoading } = useNodeEventQuery({ eventId });
  const { nodeUserData: nodeViewerId } = useUser();

  const hasFeedback = Boolean(data?.participants.find((participant) => participant.ref === nodeViewerId._id)?.feedback);

  const onSubmit = (eventFeedback: EventParticipantFeedback) => {
    createTelehealthEventFeedback(
      { data: eventFeedback, eventId, viewerId: nodeViewerId._id },
      {
        onError(error) {
          handleAxiosError(error, 'Failed to send feedback.');
        },
        onSuccess() {
          message.success('Thank you for your feedback.');
          onSuccess();
        },
      },
    );
  };

  const renderModalContent = () => {
    if (isLoading || !data) {
      return <Skeleton active />;
    }

    if (hasFeedback) {
      return <Text>Event was already rated</Text>;
    }

    return (
      <RateEventFeedbackForm<EventParticipantFeedback>
        config={config}
        loading={isCreatingEventFeedback}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    );
  };

  return (
    <Modal isOpened={isOpened} maskClosable={false} onCancel={onCancel} title="Rate Event Experience" width="medium">
      <Modal.HugContent>{renderModalContent()}</Modal.HugContent>
    </Modal>
  );
};

export default RateEventFeedbackModal;
